import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '../_shared/services';
import { StatusResponse } from "../_shared/interfaces";

import PackageJson from "../../../package.json";
import { LanguageSelectorComponent } from "../core/language-selector/language-selector.component";
import {
  ClrAlertModule,
  ClrCheckboxModule,
  ClrDropdownModule,
  ClrIconModule,
  ClrInputModule,
  ClrPasswordModule,
  ClrSelectModule,
  ClrSpinnerModule
} from "@clr/angular";
import { DatePipe, JsonPipe, NgClass, NgForOf, NgIf } from "@angular/common";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { forkJoin } from "rxjs";

import { environment } from "../../environments/environment";
import { BrandingAssets, BrandingService } from "../_shared/services/branding.service";

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    LanguageSelectorComponent,
    ClrAlertModule,
    ClrInputModule,
    ClrPasswordModule,
    NgIf,
    ClrSpinnerModule,
    ClrIconModule,
    TranslocoDirective,
    NgClass,
    NgForOf,
    ClrDropdownModule,
    ClrSelectModule,
    FormsModule,
    ClrCheckboxModule
    ,
    DatePipe,
    JsonPipe
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.sass'
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup = new FormGroup<any>({});
  errorStatus = false;
  healthStatus = false;
  loading = false;
  private authService: AuthService;

  public devMode: boolean;

  public stage: string;
  currentLang = ''; // ✅ Diese Zeile fügt die fehlende Variable hinzu
  public branding: BrandingAssets;
  authDebugReason: { reason: string; message: string; details?: Record<string, unknown>; at: string } | null = null;

  public ppsversion: string = PackageJson.version;

  healthChecks: HealthCheck[] = [
    { key: 'rms', label: 'RMS', status: 'pending' },
    { key: 'auth', label: 'PPS Auth', status: 'pending' },
    ...(environment.externalCrm ? [{ key: 'cms-auth', label: 'CRM Auth', status: 'pending' as HealthStatus }] : []),
    { key: 'minio', label: 'MinIO', status: 'pending' },
    { key: 'db', label: 'DB', status: 'pending' }
  ];

  iconShape(status: HealthStatus): string {
    switch (status) {
      case 'ok':
        return 'success-standard';
      case 'fail':
        return 'error-standard';
      case 'pending':
        return 'clock';
      case 'unknown':
      default:
        return 'help';
    }
  }

  constructor(
    authService: AuthService,
    private brandingService: BrandingService,
    private translocoService: TranslocoService

  ) {
    this.authService = authService;
    this.devMode = environment.stages.dev;
    this.stage = environment.stages.name;
    this.branding = this.brandingService.getAssets();
  }

  ngOnInit() {
    this.sysCheck();
    this.authDebugReason = this.authService.getLoginDebugReason();
    this.loginForm = new FormGroup({
      username: new FormControl(''),
      password: new FormControl('')
    });

    this.loginForm.valueChanges.subscribe(() => {
      if (this.errorStatus) {
        this.errorStatus = false;
      }
    });
  }

  loginUser() {
    const formValues = this.loginForm.value;
    this.loading = true;
    this.authService.signIn(formValues.username, formValues.password)
      .subscribe({
        next: () => {
          this.loading = false;
          this.errorStatus = false;
        },
        error: (err) => {
          this.loading = false;
          this.errorStatus = true;
          // Bei 401 (Unauthorized) Formular zurücksetzen
          if (err && (err.status === 401 || err.statusCode === 401)) {
            this.loginForm.reset();
          }

          // Hinweis: Anzeige erfolgt über die Inline-Fehlermeldung im Template
        }
      });
  }

  sysCheck() {
    this.updateHealthStatus('rms', 'pending');
    this.updateHealthStatus('auth', 'pending');
    if (environment.externalCrm) {
      this.updateHealthStatus('cms-auth', 'pending');
    }
    this.updateHealthStatus('db', 'pending');
    this.updateHealthStatus('minio', 'pending');

    const rms$ = this.authService.rmsStatus();

    const auth$ = this.authService.authHealthCheck();

    // Auf der Login-Seite wird das CRM bewusst nicht geprueft. Die Pruefung
    // braucht eine CRM-Sitzung, und die gibt es hier nur als Rest der zuvor
    // angemeldeten Person -- auf einem geteilten Rechner also die eines
    // Fremden. Das gruene Haekchen sagte damit nichts ueber den eigenen
    // Zugang aus.
    forkJoin({ rms: rms$, auth: auth$ }).subscribe(({ rms, auth }) => {
      this.applyRmsStatus(rms);
      this.updateHealthStatus('auth', auth ? 'ok' : 'fail');
      if (environment.externalCrm) {
        this.updateHealthStatus('cms-auth', 'unknown');
        this.updateHealthNote('cms-auth', 'erst nach Anmeldung');
      }

      this.healthStatus = !!rms?.ok && auth;
    });
  }

  private updateHealthStatus(key: string, status: HealthStatus) {
    const item = this.healthChecks.find((check) => check.key === key);
    if (item) {
      item.status = status;
    }
  }

  private updateHealthNote(key: string, note?: string) {
    const item = this.healthChecks.find((check) => check.key === key);
    if (item) {
      item.note = note;
    }
  }

  private applyRmsStatus(status: StatusResponse | null) {
    if (!status) {
      this.updateHealthStatus('rms', 'fail');
      this.updateHealthStatus('db', 'unknown');
      this.updateHealthStatus('minio', 'unknown');
      this.updateHealthNote('minio', 'keine Antwort');
      return;
    }

    this.updateHealthStatus('rms', status.rms?.ok === false ? 'fail' : 'ok');
    this.updateHealthStatus('db', status.db?.ok ? 'ok' : 'fail');
    if (!status.db?.ok && status.db?.error) {
      this.updateHealthNote('db', status.db.error);
    }

    if (status.minio?.configured === false) {
      this.updateHealthStatus('minio', 'unknown');
      this.updateHealthNote('minio', 'nicht konfiguriert');
    } else {
      this.updateHealthStatus('minio', status.minio?.ok ? 'ok' : 'fail');
      const details = [status.minio?.endpoint, status.minio?.bucket]
        .filter(Boolean)
        .join(' · ');
      this.updateHealthNote('minio', details || status.minio?.error || undefined);
    }
  }

  public changeLanguage(languageCode: string): void {
    this.translocoService.setActiveLang(languageCode);
    languageCode === 'fa'
      ? (document.body.style.direction = 'rtl')
      : (document.body.style.direction = 'ltr');
  }

  dismissAuthDebugReason() {
    this.authService.clearLoginDebugReason();
    this.authDebugReason = null;
  }
}

type HealthStatus = 'ok' | 'fail' | 'unknown' | 'pending';

interface HealthCheck {
  key: string;
  label: string;
  status: HealthStatus;
  note?: string;
}
